import * as XLSX from "xlsx";

export const REQUIRED_SIENGE_FIELDS = [
  "empresa",
  "obra",
  "unidade",
  "mes_ano",
  "vgv",
  "variacao_vgv",
  "poc",
  "variacao_poc",
  "receita_apropriada",
  "receita_apropriada_mensal",
  "contas_receber_adto_cliente",
  "recebimento_acumulado",
  "recebimento_mensal",
  "recebimento_unidade_distratada",
  "custo_orcado",
  "variacao_custo_orcado",
  "incorrido_acumulado",
  "custo_a_incorrer",
  "incorrido_mensal",
  "custo_apropriado",
  "custo_apropriado_mensal",
  "estoque",
  "provisao_garantia",
  "comissoes",
  "receita_acumulada",
  "custo_acumulado",
  "lucro_bruto",
  "margem_percentual",
];

export const SIENGE_TEMPLATE_HEADER_BY_INDEX = {
  0: "empresa",
  1: "obra",
  2: "unidade",
  3: "mes_ano",
  5: "vgv",
  6: "variacao_vgv",
  7: "poc",
  8: "variacao_poc",
  10: "receita_apropriada",
  11: "receita_apropriada_mensal",
  12: "contas_receber_adto_cliente",
  14: "recebimento_acumulado",
  15: "recebimento_mensal",
  16: "recebimento_unidade_distratada",
  18: "custo_orcado",
  19: "variacao_custo_orcado",
  20: "incorrido_acumulado",
  21: "custo_a_incorrer",
  22: "incorrido_mensal",
  24: "custo_apropriado",
  25: "custo_apropriado_mensal",
  26: "estoque",
  27: "provisao_garantia",
  28: "comissoes",
  30: "receita_acumulada",
  31: "custo_acumulado",
  32: "lucro_bruto",
  33: "margem_percentual",
};

const SIENGE_TEMPLATE_HEADER_ALIASES_BY_INDEX = {
  12: ["contas_a_receber_e_ou_adto_cliente"],
  27: ["provisao_de_garantia"],
  33: ["percentual"],
};

const MAX_HEADER_SCAN_ROWS = 4;
const SUPPORTED_IMPORT_EXTENSIONS = ["csv", "xlsx", "xls"];
const IMPORT_PROGRESS_DELAY_MS = 160;

function normalizeText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[%]/g, " percentual ")
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .toLowerCase();
}

function toNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return 0;
  }

  const compact = normalized.replace(/\s/g, "");
  const lastCommaIndex = compact.lastIndexOf(",");
  const lastDotIndex = compact.lastIndexOf(".");

  let numericText = compact;

  if (lastCommaIndex >= 0 && lastDotIndex >= 0) {
    numericText =
      lastDotIndex > lastCommaIndex
        ? compact.replace(/,/g, "")
        : compact.replace(/\./g, "").replace(",", ".");
  } else if (lastCommaIndex >= 0) {
    const commaOccurrences = (compact.match(/,/g) || []).length;
    numericText =
      commaOccurrences > 1 || /^-?\d{1,3}(,\d{3})+$/.test(compact)
        ? compact.replace(/,/g, "")
        : compact.replace(",", ".");
  } else if (lastDotIndex >= 0) {
    const dotOccurrences = (compact.match(/\./g) || []).length;
    numericText =
      dotOccurrences > 1 && /^-?\d{1,3}(\.\d{3})+$/.test(compact)
        ? compact.replace(/\./g, "")
        : compact;
  }

  const amount = Number(numericText);
  return Number.isFinite(amount) ? amount : 0;
}

function toStringValue(value) {
  return String(value ?? "").trim();
}

function toMesAno(value) {
  if (value === "" || value === null || value === undefined) {
    return "";
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const parsedDate = XLSX.SSF.parse_date_code(value);
    if (parsedDate?.m && parsedDate?.y) {
      return `${String(parsedDate.m).padStart(2, "0")}/${parsedDate.y}`;
    }
  }

  const normalized = toStringValue(value).replace(/[-.]/g, "/");
  const match = normalized.match(/^(\d{1,2})\/(\d{2,4})$/);
  if (!match) {
    return normalized;
  }

  const month = String(Number(match[1]) || "").padStart(2, "0");
  const year = match[2].length === 2 ? `20${match[2]}` : match[2];
  return month && year ? `${month}/${year}` : normalized;
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function getFileExtension(fileName) {
  const normalizedName = toStringValue(fileName);
  const nameParts = normalizedName.split(".");
  return nameParts.length > 1 ? nameParts[nameParts.length - 1].toLowerCase() : "";
}

function validateImportFile(file) {
  if (!file) {
    throw new Error("Nenhum arquivo foi selecionado para importacao.");
  }

  const fileExtension = getFileExtension(file.name);

  if (!SUPPORTED_IMPORT_EXTENSIONS.includes(fileExtension)) {
    throw new Error(
      "Formato de arquivo invalido. Utilize um arquivo CSV, XLSX ou XLS."
    );
  }

  if (!file.size) {
    throw new Error("O arquivo selecionado esta vazio. Escolha uma planilha valida.");
  }

  return fileExtension;
}

async function emitImportProgress(onProgress, payload) {
  if (typeof onProgress !== "function") {
    return;
  }

  onProgress(payload);
  await sleep(payload.delayMs ?? IMPORT_PROGRESS_DELAY_MS);
}

function findHeaderRowIndex(rows) {
  const limit = Math.min(rows.length, MAX_HEADER_SCAN_ROWS);

  for (let rowIndex = 0; rowIndex < limit; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    const headerSignature = [
      normalizeText(row[0]),
      normalizeText(row[1]),
      normalizeText(row[2]),
      normalizeText(row[3]),
    ].join("|");

    if (headerSignature === "empresa|obra|unidade|mes_ano") {
      return rowIndex;
    }
  }

  return 1;
}

function getMissingHeaders(row) {
  return Object.entries(SIENGE_TEMPLATE_HEADER_BY_INDEX)
    .filter(([index, expectedHeader]) => {
      const normalizedHeader = normalizeText(row[Number(index)]);
      const acceptedHeaders = [
        expectedHeader,
        ...(SIENGE_TEMPLATE_HEADER_ALIASES_BY_INDEX[Number(index)] || []),
      ];

      return !acceptedHeaders.includes(normalizedHeader);
    })
    .map(([index, expectedHeader]) => ({
      index: Number(index),
      expectedHeader,
      receivedHeader: toStringValue(row[Number(index)]),
    }));
}

export async function parseSiengeFile(file, options = {}) {
  const { onProgress } = options;
  const fileExtension = validateImportFile(file);

  await emitImportProgress(onProgress, {
    stepId: "file",
    progress: 14,
    detail: `Arquivo ${fileExtension.toUpperCase()} recebido e liberado para leitura.`,
  });

  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    throw new Error("A planilha selecionada nao possui abas legiveis.");
  }

  const firstSheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" });

  await emitImportProgress(onProgress, {
    stepId: "sheet",
    progress: 34,
    detail: `Aba ${sheetName} localizada com ${rows.length} linha(s) para analise.`,
  });

  const headerRowIndex = findHeaderRowIndex(rows);
  const headerRow = rows[headerRowIndex] || [];
  const missingHeaders = getMissingHeaders(headerRow);

  if (missingHeaders.length) {
    const preview = missingHeaders
      .slice(0, 4)
      .map((item) => `${item.expectedHeader} (coluna ${item.index + 1})`)
      .join(", ");

    throw new Error(
      `Template do MAPA/Sienge invalido. Cabecalhos esperados nao encontrados: ${preview}.`
    );
  }

  await emitImportProgress(onProgress, {
    stepId: "headers",
    progress: 56,
    detail: `Cabecalhos do MAPA/Sienge confirmados na linha ${headerRowIndex + 1}.`,
  });

  const eligibleRows = rows
    .slice(headerRowIndex + 1)
    .filter((row) => toStringValue(row[0]) !== "" && toStringValue(row[0]) !== "Empresa");

  const records = eligibleRows.map((row) => ({
      empresa: toStringValue(row[0]),
      obra: toStringValue(row[1]),
      unidade: toStringValue(row[2]),
      mes_ano: toMesAno(row[3]),
      vgv: toNumber(row[5]),
      variacao_vgv: toNumber(row[6]),
      poc: toNumber(row[7]),
      variacao_poc: toNumber(row[8]),
      receita_apropriada: toNumber(row[10]),
      receita_apropriada_mensal: toNumber(row[11]),
      contas_receber_adto_cliente: toNumber(row[12]),
      recebimento_acumulado: toNumber(row[14]),
      recebimento_mensal: toNumber(row[15]),
      recebimento_unidade_distratada: toNumber(row[16]),
      custo_orcado: toNumber(row[18]),
      variacao_custo_orcado: toNumber(row[19]),
      incorrido_acumulado: toNumber(row[20]),
      custo_a_incorrer: toNumber(row[21]),
      incorrido_mensal: toNumber(row[22]),
      custo_apropriado: toNumber(row[24]),
      custo_apropriado_mensal: toNumber(row[25]),
      estoque: toNumber(row[26]),
      provisao_garantia: toNumber(row[27]),
      comissoes: toNumber(row[28]),
      receita_acumulada: toNumber(row[30]),
      custo_acumulado: toNumber(row[31]),
      lucro_bruto: toNumber(row[32]),
      margem_percentual: toNumber(row[33]),
    }));

  if (!records.length) {
    throw new Error("Nenhum registro valido foi encontrado no arquivo selecionado.");
  }

  await emitImportProgress(onProgress, {
    stepId: "rows",
    progress: 78,
    detail: `${records.length} registro(s) elegiveis foram normalizados para carga no portal.`,
  });

  return {
    records,
    totalRows: rows.length,
    eligibleRows: eligibleRows.length,
    fileExtension,
    sheetName,
    headerRowIndex,
  };
}
